
import React, { useState } from 'react';
import { PredefinedAction } from '@/types/action';
import { PredefinedActionsService } from '@/api/services/predefinedActionsService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Tag, Play, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface PredefinedActionCardProps {
  action: PredefinedAction;
  onClick?: (action: PredefinedAction) => void;
}

export const PredefinedActionCard: React.FC<PredefinedActionCardProps> = ({
  action,
  onClick
}) => {
  const [isEnabling, setIsEnabling] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleEnableAction = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      setIsEnabling(true);
      
      const response = await PredefinedActionsService.enablePredefinedAction(action.id);
      
      if (response.status === 'success') {
        toast({
          title: "Action Enabled Successfully!",
          description: `${action.name} is now available in app connections to connect.`,
          variant: "default",
        });
        
        // Optional: Call the onClick handler if provided
        onClick?.(action);
      } else {
        throw new Error(response.message || 'Failed to enable action');
      }
    } catch (error) {
      console.error('Error enabling predefined action:', error);
      toast({
        title: "Failed to Enable Action",
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
        variant: "destructive",
      });
    } finally {
      setIsEnabling(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      'Productivity': 'bg-blue-50 text-blue-700 border-blue-200',
      'Communication': 'bg-green-50 text-green-700 border-green-200',
      'Data': 'bg-purple-50 text-purple-700 border-purple-200',
      'Analytics': 'bg-orange-50 text-orange-700 border-orange-200',
      'Integration': 'bg-pink-50 text-pink-700 border-pink-200',
      'Automation': 'bg-indigo-50 text-indigo-700 border-indigo-200',
      'Marketing': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'Finance': 'bg-emerald-50 text-emerald-700 border-emerald-200',
      'HR': 'bg-rose-50 text-rose-700 border-rose-200',
      'Sales': 'bg-cyan-50 text-cyan-700 border-cyan-200'
    };
    return colors[category] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <Card 
      className="h-full hover:shadow-md transition-all duration-200 cursor-pointer border-gray-200 hover:border-gray-300"
      onClick={() => onClick?.(action)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base font-semibold text-gray-900 truncate">
              {action.name}
            </CardTitle>
            <div className="flex items-center gap-2 mt-1">
              {action.category && (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${getCategoryColor(action.category)}`}
                >
                  <Tag className="h-3 w-3 mr-1" />
                  {action.category}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <CardDescription className="text-sm text-gray-600 line-clamp-3 mb-4">
          {action.description || 'No description available for this action.'}
        </CardDescription>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar className="h-3 w-3" />
            <span>Created {formatDate(action.created_at)}</span>
          </div>
          
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span>ID: {action.id}</span>
            </div>
            
            <Button 
              size="sm" 
              variant="outline" 
              className="h-7 px-2 text-xs"
              onClick={handleEnableAction}
              disabled={isEnabling}
            >
              {isEnabling ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Play className="h-3 w-3 mr-1" />
              )}
              {isEnabling ? 'Enabling...' : 'Enable Action'}
            </Button>
          </div>
          
          {(action.integration_id || action.integration_id_2) && (
            <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100">
              {action.integration_id && (
                <Badge variant="secondary" className="text-xs px-2 py-0.5">
                  Integration: {action.integration_id}
                </Badge>
              )}
              {action.integration_id_2 && action.integration_id_2 !== action.integration_id && (
                <Badge variant="secondary" className="text-xs px-2 py-0.5">
                  {action.integration_id_2}
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
